import { calculateSecurityScore, SecurityScoreResult } from "@/lib/securityScore";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Shield, CheckCircle, AlertTriangle, XCircle, HelpCircle } from "lucide-react";

const GRADE_STYLES: Record<string, string> = {
  A: "text-success",
  B: "text-chart-1",
  C: "text-warning",
  D: "text-orange-500",
  F: "text-destructive",
};

const GRADE_BG: Record<string, string> = {
  A: "bg-success/15 border-success/30",
  B: "bg-chart-1/15 border-chart-1/30",
  C: "bg-warning/15 border-warning/30",
  D: "bg-orange-500/15 border-orange-500/30",
  F: "bg-destructive/15 border-destructive/30",
};

const STATUS_ICON = {
  pass: <CheckCircle className="h-3 w-3 text-success" />,
  warning: <AlertTriangle className="h-3 w-3 text-warning" />,
  fail: <XCircle className="h-3 w-3 text-destructive" />,
  unknown: <HelpCircle className="h-3 w-3 text-muted-foreground" />,
};

interface SecurityScoreBadgeProps {
  server: any;
  compact?: boolean;
}

export function SecurityScoreBadge({ server, compact = false }: SecurityScoreBadgeProps) {
  const result = calculateSecurityScore(server);

  if (compact) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-xs font-bold cursor-pointer hover:opacity-80 transition-opacity ${GRADE_BG[result.grade]}`}>
            <Shield className={`h-3 w-3 ${GRADE_STYLES[result.grade]}`} />
            <span className={GRADE_STYLES[result.grade]}>{result.score}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <SecurityScoreDetail result={result} />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg border cursor-pointer hover:opacity-80 transition-opacity ${GRADE_BG[result.grade]}`}>
          <div className="flex items-center gap-1">
            <Shield className={`h-4 w-4 ${GRADE_STYLES[result.grade]}`} />
            <span className={`text-lg font-bold ${GRADE_STYLES[result.grade]}`}>{result.score}</span>
          </div>
          <span className="text-[10px] text-muted-foreground">Security Score</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <SecurityScoreDetail result={result} />
      </PopoverContent>
    </Popover>
  );
}

function SecurityScoreDetail({ result }: { result: SecurityScoreResult }) {
  return (
    <div className="space-y-3 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className={`h-5 w-5 ${GRADE_STYLES[result.grade]}`} />
          <span className="font-semibold text-sm">Security Score</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-2xl font-bold ${GRADE_STYLES[result.grade]}`}>{result.score}</span>
          <Badge variant="outline" className={`text-xs font-bold ${GRADE_BG[result.grade]} ${GRADE_STYLES[result.grade]}`}>
            {result.grade}
          </Badge>
        </div>
      </div>

      {/* Category breakdown */}
      <div className="space-y-2">
        {result.breakdown.map((cat) => {
          const pct = cat.max > 0 ? Math.round((cat.earned / cat.max) * 100) : 0;
          return (
            <div key={cat.category} className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="capitalize text-muted-foreground">{cat.category}</span>
                <span className="font-medium">{cat.earned}/{cat.max}</span>
              </div>
              <Progress value={pct} className="h-1.5" />
            </div>
          );
        })}
      </div>

      {/* Individual checks */}
      <div className="border-t pt-2 space-y-1.5">
        <span className="text-xs font-medium text-muted-foreground">Checks</span>
        {result.checks.map((check, i) => (
          <div key={i} className="flex items-start gap-2 text-xs">
            {STATUS_ICON[check.status]}
            <div className="flex-1">
              <span className="font-medium">{check.check}</span>
              <p className="text-muted-foreground">{check.detail}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
